import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { OcrNumber } from '@/types/ocr-numbers'
import type { SettlementDocument } from '@/types/settlement-documents'

// Store for the APP-GLOBAL singleton dialogs (S-tier, mounted once in App.vue, opened
// decoupled from many places — cards, the "…" menu, the detail view, legacy Summary,
// action flows). Replaces the legacy Vuex dialog keys as the dialogs go native
// (ADA-4437 for five of them, ADA-4432 for `payment`).
//
// NOTE: the payment dialog is the one member mounted while LOGGED OUT (pay-with-OCR from the
// login screen), so `reset()` clearing it on an auth transition is what closes it there.
//
// SCOPE RULE: view-local dialogs do NOT belong here — a dialog with a single trigger in
// a single view uses `BDialog` directly with a local v-model.
//
// One combined store is a DELIBERATE, documented deviation from ada-app-ui's
// per-concern pattern (routeGuardStore ↔ SRouteGuardDialog): six small dialogs that
// share payload types and ONE auth reset (useAuthEffects) read better combined here
// (Anton, ADA-4437 beslut 8; the legacy dialog bridge that also used it is gone).

/**
 * Payload to the media viewer. `fileId` is what legacy calls bucket_id (the file id in the
 * bucket). Senders today: prints (demand letters, debt summaries), cases (original
 * invoices), ticket--messages (support attachments via media_file_chips).
 */
export interface MediaDialogPayload {
  resource: 'prints' | 'cases' | 'ticket--messages'
  documentId: string
  fileId: string
  contentType?: string
}

/** `title`/`text` are locale KEYS — the dialog translates them. */
export interface MessageDialogPayload {
  title: string
  text: string
}

export interface ProfileDialogPayload {
  context: 'home' | 'request_invoice_copy'
}

export interface RespiteParams {
  days: { min: number; max: number }
  schedule_to_execute: string
  id: string
}

export interface RespiteDialogPayload {
  document: SettlementDocument
  params: RespiteParams
  /**
   * OCR-number slice (amount / currency / creditor_name) for the date page's summary. Legacy read
   * these off the pasted `document.ocr`; native takes them from the useOcrNumbers data instead,
   * supplied by the action menu/row (etapp 6). Optional: the summary degrades gracefully if the
   * join has not resolved.
   */
  ocr?: OcrNumber
}

export interface AmortizationParams {
  id: string
  max_number_of_advices?: number
  max_number_of_advices_intervals?: { from_amount: number; to_amount: number; max_number_of_advices: number }[]
  advice_date_days_before_due_date: number
}

/**
 * Payment dialog (ADA-4432). BOTH fields absent = the logged-out entry from the login screen,
 * where the payer identifies the debt by typing an OCR/reference number instead — that is the
 * only case where the dialog opens on its OCR step (legacy signalled it with `{ocr: {ocr: ''}}`).
 */
export interface PaymentDialogPayload {
  /** The document the payment targets. */
  document?: SettlementDocument
  /** The OCR the money goes to — amortization-overridden by the caller (the outstanding advice,
   *  not the plan-level OCR), mirroring what the cards already pass to the action gates. */
  ocr?: OcrNumber
  /** `/ocr/{ocr}` deep link: prefills the OCR step and submits it without waiting for a click. */
  suggestedOcr?: string
}

export interface AmortizationDialogPayload {
  /** Set by the eligibility gate (single mode) / the Select step (multi mode). */
  params?: AmortizationParams
  /** Single mode: the document the plan is created for. */
  document?: SettlementDocument
  /** Multi mode: candidate documents + the ids selected so far. */
  multiple?: { documents: SettlementDocument[]; selected: string[] }
}

export const useDialogsStore = defineStore('dialogs', () => {
  const media = ref<MediaDialogPayload | null>(null)
  const message = ref<MessageDialogPayload | null>(null)
  const profile = ref<ProfileDialogPayload | null>(null)
  const respite = ref<RespiteDialogPayload | null>(null)
  const amortization = ref<AmortizationDialogPayload | null>(null)
  const payment = ref<PaymentDialogPayload | null>(null)
  // Replaces the Vuex `dont_show_profile_dialog` flag (hydrated by useLegacyBootstrap
  // today). Gates ONLY the Home profile nudge — `openProfile()` always opens (the legacy
  // gate also blocked the request_invoice_copy flow, a bug fixed deliberately in etapp 3).
  // The dialog persists the flag to sessionStorage on close (wired in etapp 3).
  const dontShowProfileDialog = ref(sessionStorage.getItem('dont_show_profile_dialog') === 'true')

  // The overpayments nudge is queued ONCE per session. Home remounts on every return from a case
  // view, and a per-instance flag re-queued the nudge while the previous one was still showing
  // (ADA-4457 test round). Reset with the rest of the store on logout / user switch.
  const overpaymentNudgeQueued = ref(false)

  // request_invoice_copy awaits the profile dialog's close before re-checking the mobile
  // number (replaces the legacy actions_mixin watcher-Promise on the Vuex key).
  let profileClosedResolver: (() => void) | null = null
  let profileClosedPromise: Promise<void> | null = null
  function openProfile(payload: ProfileDialogPayload): Promise<void> {
    profile.value = payload
    // A second open before close must not replace the resolver — the first caller's
    // promise would never settle. Everyone awaits the same close.
    profileClosedPromise ??= new Promise((resolve) => {
      profileClosedResolver = resolve
    })
    return profileClosedPromise
  }

  function closeProfile() {
    profile.value = null
    profileClosedResolver?.()
    profileClosedResolver = null
    profileClosedPromise = null
  }

  /** Auth transitions (useAuthEffects): close everything. Native counterpart of the LEGACY_DIALOG_STATE reset. */
  function reset() {
    media.value = null
    message.value = null
    respite.value = null
    amortization.value = null
    payment.value = null
    overpaymentNudgeQueued.value = false
    // An identity change must also drop the previous user's nudge suppression — BOTH the
    // in-memory flag and its sessionStorage backing. useAuthEffects.resetLegacyState() clears
    // storage too, but only on the logout path (and it early-returns on public routes), so a
    // login synced from another tab would otherwise re-hydrate the old flag on reload and
    // silence the nudge for the new user (PR #895 review).
    dontShowProfileDialog.value = false
    sessionStorage.removeItem('dont_show_profile_dialog')
    closeProfile()
  }

  return {
    media,
    message,
    profile,
    respite,
    amortization,
    payment,
    dontShowProfileDialog,
    overpaymentNudgeQueued,
    openProfile,
    closeProfile,
    reset,
  }
})
